import { Component, Input, OnInit } from '@angular/core';
import { CtModelConfiguration, CtModelOperator, CtModelType } from '@ctsolution/ct-framework';
import { SchedulerRouteData } from '../../../_core/route-data/schedule.route-data';
import { DEFAULT_DATATABLE_CONFIGURATION } from '../../../_core/constants';
import { SchedulerListConfiguration } from './scheduler-list.configuration';
import { SchedulerService } from '../scheduler.service';
import { Router } from '@angular/router';
import { CTMGeneralService } from '../../../_core/lib/general.service';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { SchedulerDialogComponent, SchedulerDialogData } from '../scheduler-dialog/scheduler-dialog.component';

/**
 * Componente che mostra la lista degli eventi dello scheduler in formato tabella
 * Supporta sia la navigazione classica che l'apertura in modale
 */
@Component({
  selector: 'app-scheduler-list',
  templateUrl: './scheduler-list.component.html',
  styleUrls: ['./scheduler-list.component.scss']
})
export class SchedulerListComponent implements OnInit {

  /** Configurazione del componente */
  configuration: CtModelConfiguration<any> | null = null;

  /** Configurazione passata dal parent (es. lead-edit) */
  @Input() schedulerListConfiguration: SchedulerListConfiguration | null = null;

  constructor(
    private schedulerService: SchedulerService,
    private router: Router,
    private generalService: CTMGeneralService,
    private dialog: MatLegacyDialog
  ) { }

  ngOnInit() {
    this.setup()
  }

  /**
   * Inizializza la configurazione del datatable degli eventi
   * Applica i filtri e imposta i callbacks per creazione/modifica
   */
  setup() {
    const configuration = CtModelConfiguration.create()
      .setRouteData(SchedulerRouteData().setModelType(CtModelType.DATATABLE))
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION())
      .setDisableAutoQueryFilter(true);

    // Applica i filtri costanti (es. Lead.Oid = X)
    if (this.schedulerListConfiguration?.Filters) {
      this.schedulerListConfiguration?.Filters.forEach(filter => {
        configuration
          .filterValues
          .addConstantFilter(filter)
      });
    }

    // Imposta i callbacks per creazione e modifica
    configuration
      .setOnCreate(() => this.onCreate())
      .setOnEdit((Event: any) => this.onEdit(Event))

    // Se onlyUpdate = true, mostra solo il pulsante di modifica
    if (this.schedulerListConfiguration?.onlyUpdate) {
      configuration
        .initialized()
        .subscribe(() => configuration?.setOperations([CtModelOperator.Update]));
    }

    this.configuration = configuration;
  }

  /**
   * Gestisce il click sul pulsante "Crea evento"
   * Apre lo scheduler in modale o naviga alla pagina in base alla configurazione
   */
  async onCreate() {
    // Esegue il callback prima di aprire lo scheduler (es. salva il form del lead)
    if (this.schedulerListConfiguration?.Action) {
      await this.schedulerListConfiguration.Action(null)

      // Se navigatePostAction = false, non fare nulla dopo l'action
      if (this.schedulerListConfiguration.navigatePostAction == false) return;
    }

    // NUOVO: Se useModal = true, apri la modale invece di navigare
    if (this.schedulerListConfiguration?.useModal) {
      this.openSchedulerModal(null);
      return;
    }

    // COMPORTAMENTO CLASSICO: Naviga alla pagina scheduler
    if (this.schedulerListConfiguration?.param) {
      this.schedulerService.navigateToPath(this.schedulerListConfiguration?.param)
    }
  }

  /**
   * Gestisce il click su un evento esistente nella tabella
   * Apre lo scheduler in modale o naviga alla pagina in base alla configurazione
   * @param Event Evento cliccato dalla datatable
   */
  async onEdit(Event: any) {
    // Esegue il callback prima di aprire lo scheduler
    if (this.schedulerListConfiguration?.Action) {
      await this.schedulerListConfiguration.Action(null)
    }

    // NUOVO: Se useModal = true, apri la modale con l'evento selezionato
    if (this.schedulerListConfiguration?.useModal) {
      this.openSchedulerModal(Event.Oid?.toString() || null);
      return;
    }

    // COMPORTAMENTO CLASSICO: Naviga alla pagina scheduler con queryParams
    if (!this.schedulerListConfiguration?.param) return;

    const queryParams = {
      TypeExtra: this.schedulerListConfiguration?.param.Type,
      OidExtra: this.schedulerListConfiguration?.param.Oid,
      ExtraUrl: this.schedulerListConfiguration?.extraUrlParam,
      IdEvent: Event.Oid
    };

    this.router.navigate(['scheduler', 'calendar'], {
      queryParams: queryParams
    });
  }

  /**
   * Apre lo scheduler in una modale
   * @param idEvent ID dell'evento da modificare (null per creare un nuovo evento)
   */
  private openSchedulerModal(idEvent: string | null) {
    const dialogData: SchedulerDialogData = {
      param: this.schedulerListConfiguration?.param || null,
      idEvent: idEvent,
      // Wrapper per adattare la firma del callback
      beforeOpenAction: this.schedulerListConfiguration?.Action 
        ? async () => {
            if (this.schedulerListConfiguration?.Action) {
              await this.schedulerListConfiguration.Action(null);
            }
          }
        : null
    };

    // Apri la modale con dimensioni 90% viewport
    const dialogRef = this.dialog.open(SchedulerDialogComponent, {
      width: '90vw',
      maxWidth: '90vw',
      height: '90vh',
      maxHeight: '90vh',
      disableClose: false,
      data: dialogData
    });

    // Quando la modale si chiude, ricarica la lista se necessario
    dialogRef.afterClosed().subscribe(result => {
      if (result?.refresh) {
  
           window.location.reload();
      }
    });
  }
}
